/**
 * @file expiring-columns.tsx
 * @description Колонки списка «Срок годности» для `ResponsiveList`
 * (phase19.31, зона «Склад»; разбор B — «Срок годности», uiux-design §6).
 *
 * Срок — статусом по реестру: «скоро истекает» — янтарный, «истёк» —
 * красный (без эмодзи, SC29). Количество — сумма по всем складам обычным
 * шрифтом с табличными цифрами: кладовщику важен объём к списанию.
 * Артикул и партия — коды, моноширинным.
 */

import { ExpiryPill } from "./ExpiryPill";
import type { ListColumn } from "../../ui/responsive-list-types";
import { TwoLineCell } from "../../ui/TwoLineCell";
import { formatSkuName, type LotDto } from "../../../lib/api-wms";
import { formatShopDate } from "../../../lib/shop-time";

/** Суммарный остаток партии по всем складам. */
export function lotTotalQuantity(lot: LotDto): number {
  return (lot.stockLevels ?? []).reduce((sum, l) => sum + Number(l.quantity || 0), 0);
}

/** Склады партии через запятую. */
function warehousesText(lot: LotDto): string {
  return (lot.stockLevels ?? []).map((l) => l.warehouse.name).join(", ") || "—";
}

export const EXPIRING_COLUMNS: ListColumn<LotDto>[] = [
  {
    key: "product",
    header: "Товар",
    cell: (lot) => <TwoLineCell primary={formatSkuName(lot.sku)} secondary={<span className="font-mono">{lot.sku?.code ?? "—"}</span>} />,
    compact: "title",
    compactCell: (lot) => formatSkuName(lot.sku),
  },
  {
    key: "lot",
    header: "Партия",
    width: "w-[184px]",
    cell: (lot) => <span className="type-body block truncate font-mono">{lot.batchNumber}</span>,
    compact: "subtitle",
    compactCell: (lot) => (
      <>
        <span className="font-mono">{lot.batchNumber}</span>
        {lot.expiresAt ? ` · до ${formatShopDate(lot.expiresAt)}` : null}
      </>
    ),
    fold: { into: "product" },
  },
  {
    key: "expiresAt",
    header: "Годен до",
    width: "w-[120px]",
    cell: (lot) => (lot.expiresAt ? formatShopDate(lot.expiresAt) : "—"),
    fold: { into: "status", cell: (lot) => (lot.expiresAt ? `до ${formatShopDate(lot.expiresAt)}` : null) },
  },
  {
    key: "status",
    header: "Срок",
    width: "w-[168px]",
    cell: (lot) => (lot.expiresAt ? <ExpiryPill expiresAt={lot.expiresAt} /> : "—"),
    compact: "trailing",
  },
  {
    key: "quantity",
    header: "Кол-во",
    width: "w-[96px]",
    align: "end",
    cell: (lot) => <span className="tabular-nums">{lotTotalQuantity(lot).toLocaleString("ru-RU")}</span>,
    compact: "meta",
    compactCell: (lot) => `${lotTotalQuantity(lot).toLocaleString("ru-RU")} шт`,
  },
  {
    key: "warehouses",
    header: "Склады",
    width: "w-[200px]",
    cell: (lot) => <span className="block truncate text-on-surface-variant">{warehousesText(lot)}</span>,
    compact: "meta",
    compactCell: warehousesText,
    fold: { into: "quantity", cell: warehousesText },
  },
];
